import {
  type ListingMenuKind,
  type MenuScanWarning,
  menuScanEventSchema,
  type ScannedMenu,
} from '@pulsify/shared/dto'
import { type ErrorResponse, ListingMenuErrorCode } from '@pulsify/shared/errors'
import { NetworkError, NetworkErrorTypes } from '@pulsify/shared/network-error'
import { adminBypassHeaders } from '~/app/admin-bypass'
import { api, getAuthToken } from '~/shared/lib/rpc'
import { NdjsonLineError, readNdjson } from '../lib/read-ndjson'

interface ScanMenuParams {
  businessId: string
  listingId: string
  kind: ListingMenuKind
  files: readonly File[]
  signal?: AbortSignal
  /** Called with the number of dishes recognised so far. */
  onProgress?: (dishes: number) => void
}

interface ScanMenuResult {
  menu: ScannedMenu
  warnings: MenuScanWarning[]
}

function streamError(code: string): NetworkError {
  const error = new NetworkError(NetworkErrorTypes.SERVER_ERROR, undefined, code)
  error.code = code
  return error
}

async function refusal(res: Response): Promise<NetworkError> {
  const body: ErrorResponse = await res
    .json()
    .catch(() => ({ code: 'InternalError', message: 'Request failed' }))
  return NetworkError.fromWire(res.status, body)
}

async function followEvents(
  body: ReadableStream<Uint8Array>,
  onProgress: ((dishes: number) => void) | undefined,
): Promise<ScanMenuResult> {
  for await (const line of readNdjson(body)) {
    const event = menuScanEventSchema.safeParse(line)
    if (!event.success) throw streamError(ListingMenuErrorCode.MenuScanFailed)
    if (event.data.type === 'error') throw streamError(event.data.code)
    if (event.data.type === 'result') {
      return { menu: event.data.menu, warnings: event.data.warnings }
    }
    onProgress?.(event.data.dishes)
  }
  throw streamError(ListingMenuErrorCode.MenuScanFailed)
}

/**
 * Sends menu photos or a PDF to be read and follows the streamed answer to its result.
 * Rejects with a `NetworkError` carrying the API error code, or with the fetch `AbortError`
 * once `signal` aborts.
 */
export async function scanMenu({
  businessId,
  listingId,
  kind,
  files,
  signal,
  onProgress,
}: ScanMenuParams): Promise<ScanMenuResult> {
  const url = api.api.businesses[':businessId'].listings[':id'].menus.scan.$url({
    param: { businessId, id: listingId },
  })
  const body = new FormData()
  body.append('kind', kind)
  for (const file of files) body.append('files', file)
  const token = await getAuthToken()
  const headers: Record<string, string> = { ...adminBypassHeaders() }
  if (token) headers.Authorization = `Bearer ${token}`
  const res = await fetch(url, { method: 'POST', body, headers, signal })
  if (!res.ok) throw await refusal(res)
  if (!res.body) throw streamError(ListingMenuErrorCode.MenuScanFailed)
  try {
    return await followEvents(res.body, onProgress)
  } catch (error) {
    if (error instanceof NdjsonLineError) throw streamError(ListingMenuErrorCode.MenuScanFailed)
    throw error
  }
}
